import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, Search } from "lucide-react";

const rupee = (v) => "₹ " + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const statusColor = {
  paid: "bg-green-100 text-green-800 border-green-200",
  partial: "bg-amber-100 text-amber-800 border-amber-200",
  unpaid: "bg-neutral-100 text-neutral-700 border-neutral-200",
  overdue: "bg-red-100 text-red-800 border-red-200",
  returned: "bg-slate-200 text-slate-700 border-slate-300",
};
const blankItem = { item: "", qty: 1, rate: 0, discount_pct: 0, tax_pct: 18, product_id: "" };

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [statusF, setStatusF] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [newCustomerMode, setNewCustomerMode] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: "", phone: "", whatsapp: "" });
  const [items, setItems] = useState([{ ...blankItem }]);
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const plusDaysISO = (n) => new Date(Date.now() + n*86400000).toISOString().slice(0, 10);
  const [date, setDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState(plusDaysISO(15));
  const [applyTax, setApplyTax] = useState(true);
  const [isIgst, setIsIgst] = useState(false);
  const [notes, setNotes] = useState("");
  const [shipping, setShipping] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [paymentMode, setPaymentMode] = useState("none");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [useWallet, setUseWallet] = useState(false);
  const [walletAmount, setWalletAmount] = useState("");

  const load = async () => {
    const params = {};
    if (statusF !== "all") params.status = statusF;
    if (search) params.search = search;
    const { data } = await api.get("/invoices", { params });
    setInvoices(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF, search]);
  useEffect(() => {
    api.get("/customers").then(r=>setCustomers(r.data));
    api.get("/products").then(r=>setProducts(r.data));
  }, []);

  const filteredCustomers = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(c =>
      (c.phone || "").toLowerCase().includes(q) ||
      (c.whatsapp || "").toLowerCase().includes(q) ||
      (c.name || "").toLowerCase().includes(q)
    );
  }, [customers, customerQuery]);

  const totals = useMemo(() => {
    let sub=0, disc=0, tax=0;
    items.forEach(it => {
      const line = Number(it.qty||0) * Number(it.rate||0);
      const d = line * (Number(it.discount_pct||0)/100);
      const tx = (line-d) * (Number(it.tax_pct||0)/100);
      sub += line; disc += d; tax += tx;
    });
    const total = sub - disc + tax + Number(shipping||0);
    return { sub, disc, tax, total };
  }, [items, shipping]);

  const selectedCustomer = useMemo(() => customers.find(c => c.id === customerId), [customers, customerId]);
  const walletBalance = Number(selectedCustomer?.wallet_balance || 0);
  const walletCap = Math.min(walletBalance, totals.total);
  const remainingAfterWallet = Math.max(0, totals.total - (useWallet ? Number(walletAmount || 0) : 0));

  useEffect(() => {
    if (useWallet) setWalletAmount(String(walletCap));
    // eslint-disable-next-line
  }, [useWallet, customerId]);

  const addRow = () => setItems([...items, { ...blankItem, tax_pct: applyTax ? 18 : 0 }]);
  const removeRow = (i) => setItems(items.filter((_,idx)=>idx!==i));
  const updateRow = (i, k, v) => setItems(items.map((it,idx)=> idx===i?{...it, [k]: k==="item"||k==="product_id"?v:Number(v||0)}:it));
  const pickProduct = (i, pid) => {
    const p = products.find(x=>x.id===pid);
    if (!p) return;
    setItems(items.map((it,idx)=> idx===i?{...it, product_id: pid, item: p.name, rate: p.price, tax_pct: applyTax ? p.tax_pct : 0, hsn: p.hsn || ""}:it));
  };
  const toggleApplyTax = (checked) => {
    setApplyTax(checked);
    setItems(items.map(it => ({ ...it, tax_pct: checked ? 18 : 0 })));
  };

  const quickAddCustomer = async () => {
    if (!newCustomer.name.trim()) return toast.error("Enter customer name");
    try {
      const { data } = await api.post("/customers", newCustomer);
      setCustomers([...customers, data]);
      setCustomerId(data.id);
      setNewCustomerMode(false);
      toast.success("Customer added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add customer"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this invoice? This cannot be undone.")) return;
    try { await api.delete(`/invoices/${id}`); toast.success("Invoice deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed (admin only)"); }
  };

  const create = async () => {
    if (submitting) return;
    if (!customerId) return toast.error("Select a customer");
    if (items.some(it => !it.item)) return toast.error("Fill item name in all rows");
    setSubmitting(true);
    try {
      const { data } = await api.post("/invoices", {
        customer_id: customerId,
        date, due_date: dueDate,
        items, is_igst: isIgst, notes, shipping: Number(shipping||0),
        payment_amount: paymentMode !== "none" ? Number(paymentAmount||0) : 0,
        payment_mode: paymentMode !== "none" ? paymentMode : null,
        apply_wallet: useWallet ? Number(walletAmount||0) : 0,
      });
      toast.success(`Invoice ${data.number} created — sending on WhatsApp…`);
      setOpen(false); setItems([{...blankItem}]); setCustomerId(""); setCustomerQuery(""); setNewCustomerMode(false); setNotes(""); setShipping(0); setPaymentMode("none"); setPaymentAmount(""); setUseWallet(false); setWalletAmount(""); setDate(todayISO()); setDueDate(plusDaysISO(15)); setApplyTax(true);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Billing</p>
          <h1 className="font-serif-elegant text-5xl mt-1">Invoices</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-[#984D38]" data-testid="new-invoice-btn"><Plus size={14} className="mr-2"/>New Invoice</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle className="font-serif-elegant text-2xl">Create Invoice</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <Label>Customer</Label>
                  {customerId ? (
                    <div className="flex items-center justify-between border rounded-md px-3 py-2 bg-neutral-50">
                      <div>
                        <p className="font-medium">{customers.find(c=>c.id===customerId)?.name}</p>
                        <p className="text-xs text-muted-foreground">{customers.find(c=>c.id===customerId)?.phone}</p>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={()=>{ setCustomerId(""); setCustomerQuery(""); setNewCustomerMode(false); setUseWallet(false); setWalletAmount(""); }}>Change</Button>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Input
                          placeholder="Enter phone number or name…"
                          value={customerQuery}
                          onChange={e=>{ setCustomerQuery(e.target.value); setCustomerPickerOpen(true); }}
                          onFocus={()=>setCustomerPickerOpen(true)}
                          onBlur={()=>setTimeout(()=>setCustomerPickerOpen(false), 150)}
                          data-testid="invoice-customer-search"
                        />
                        {customerPickerOpen && (
                          <div className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-md border bg-white shadow-md">
                            {filteredCustomers.length > 0 ? (
                              filteredCustomers.map(c => (
                                <button
                                  type="button"
                                  key={c.id}
                                  className="w-full text-left px-3 py-2 hover:bg-accent/60 flex flex-col"
                                  onMouseDown={()=>{ setCustomerId(c.id); setNewCustomerMode(false); setCustomerPickerOpen(false); setCustomerQuery(""); }}
                                  data-testid={`customer-option-${c.id}`}
                                >
                                  <span className="font-medium">{c.name}</span>
                                  {c.phone && <span className="text-xs text-muted-foreground">{c.phone}</span>}
                                  {Number(c.wallet_balance || 0) > 0 && (
                                    <span className="text-xs text-primary">Wallet: {rupee(c.wallet_balance)}</span>
                                  )}
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm space-y-2 text-left">
                                <p className="text-muted-foreground">No customer found.</p>
                                {customerQuery.trim() && (
                                  <Button
                                    type="button" size="sm" className="w-full bg-primary hover:bg-[#984D38]"
                                    onMouseDown={()=>{
                                      const looksLikePhone = /^[\d\s+()-]+$/.test(customerQuery.trim());
                                      setNewCustomer({
                                        name: looksLikePhone ? "" : customerQuery.trim(),
                                        phone: looksLikePhone ? customerQuery.trim() : "",
                                        whatsapp: looksLikePhone ? customerQuery.trim() : "",
                                      });
                                      setNewCustomerMode(true);
                                      setCustomerPickerOpen(false);
                                    }}
                                    data-testid="invoice-customer-add-new"
                                  >
                                    <Plus size={14} className="mr-2"/>Add "{customerQuery.trim()}" as new customer
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline mt-1"
                        onClick={()=>{ setNewCustomer({ name: "", phone: "", whatsapp: "" }); setNewCustomerMode(true); }}
                        data-testid="invoice-add-new-customer-link"
                      >
                        + Add a new customer instead
                      </button>
                      {newCustomerMode && (
                        <div className="mt-2 border rounded-md p-3 bg-amber-50 space-y-2">
                          <p className="text-xs text-amber-800">Quick add new customer:</p>
                          <Input placeholder="Customer name" value={newCustomer.name} onChange={e=>setNewCustomer({...newCustomer, name: e.target.value})} data-testid="invoice-new-customer-name" />
                          <Input placeholder="Phone number" value={newCustomer.phone} onChange={e=>setNewCustomer({...newCustomer, phone: e.target.value, whatsapp: e.target.value})} data-testid="invoice-new-customer-phone" />
                          <div className="flex gap-2">
                            <Button type="button" size="sm" className="bg-primary hover:bg-[#984D38]" onClick={quickAddCustomer} data-testid="invoice-new-customer-save">
                              Save & Use Customer
                            </Button>
                            <Button type="button" size="sm" variant="ghost" onClick={()=>setNewCustomerMode(false)}>Cancel</Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {customerId && walletBalance > 0 && (
                    <div className="mt-2 flex items-start gap-2 border rounded-md p-3 bg-primary/5" data-testid="wallet-apply-box">
                      <Checkbox
                        id="use-wallet"
                        checked={useWallet}
                        onCheckedChange={(v)=>setUseWallet(!!v)}
                        data-testid="use-wallet-checkbox"
                      />
                      <div className="flex-1">
                        <Label htmlFor="use-wallet" className="cursor-pointer">
                          Use wallet balance ({rupee(walletBalance)} available)
                        </Label>
                        {useWallet && (
                          <div className="mt-2">
                            <Input
                              type="number" min={0} max={walletCap}
                              value={walletAmount}
                              onChange={e=>setWalletAmount(e.target.value)}
                              data-testid="wallet-apply-amount"
                            />
                            <p className="text-xs text-muted-foreground mt-1">Up to {rupee(walletCap)} can be applied to this invoice.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-3 pt-6">
                  <div className="flex items-center gap-3">
                    <Switch id="apply-tax" checked={applyTax} onCheckedChange={toggleApplyTax} data-testid="apply-tax-toggle" />
                    <Label htmlFor="apply-tax" className="cursor-pointer">Apply GST</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch id="igst" checked={isIgst} onCheckedChange={setIsIgst} disabled={!applyTax} data-testid="igst-toggle" />
                    <Label htmlFor="igst" className={`cursor-pointer ${!applyTax ? "text-muted-foreground" : ""}`}>Inter-state (IGST)</Label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Invoice date</Label>
                  <Input type="date" value={date} onChange={e=>setDate(e.target.value)} data-testid="invoice-date" />
                </div>
                <div>
                  <Label>Due date</Label>
                  <Input type="date" value={dueDate} onChange={e=>setDueDate(e.target.value)} data-testid="invoice-due-date" />
                </div>
              </div>

              <Card className="p-3 border-border">
                <div className="grid grid-cols-12 gap-2 text-xs uppercase text-muted-foreground pb-2">
                  <div className="col-span-5">Item</div>
                  <div className="col-span-1 text-right">Qty</div>
                  <div className="col-span-2 text-right">Rate</div>
                  <div className="col-span-1 text-right">Disc%</div>
                  <div className="col-span-1 text-right">Tax%</div>
                  <div className="col-span-2 text-right">Amount</div>
                </div>
                {items.map((it, i) => {
                  const line = Number(it.qty||0)*Number(it.rate||0);
                  const d = line*(Number(it.discount_pct||0)/100);
                  const amt = (line-d) * (1 + Number(it.tax_pct||0)/100);
                  return (
                    <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
                      <div className="col-span-5 flex gap-1">
                        {products.length > 0 && (
                          <Select value={it.product_id||""} onValueChange={(v)=>pickProduct(i, v)}>
                            <SelectTrigger className="w-9 p-0 border-0 bg-transparent" data-testid={`row-product-${i}`}><SelectValue placeholder="📦"/></SelectTrigger>
                            <SelectContent>
                              {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} · {rupee(p.price)} · stock {p.stock}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                        <Input className="flex-1" placeholder="Product / service" value={it.item} onChange={e=>updateRow(i, "item", e.target.value)} data-testid={`row-item-${i}`} />
                      </div>
                      <Input className="col-span-1 text-right" type="number" value={it.qty} onChange={e=>updateRow(i, "qty", e.target.value)} data-testid={`row-qty-${i}`} />
                      <Input className="col-span-2 text-right" type="number" value={it.rate} onChange={e=>updateRow(i, "rate", e.target.value)} data-testid={`row-rate-${i}`} />
                      <Input className="col-span-1 text-right" type="number" value={it.discount_pct} onChange={e=>updateRow(i, "discount_pct", e.target.value)} />
                      <Input className="col-span-1 text-right" type="number" value={it.tax_pct} onChange={e=>updateRow(i, "tax_pct", e.target.value)} disabled={!applyTax} data-testid={`row-tax-${i}`} />
                      <div className="col-span-2 flex items-center justify-end gap-1 text-sm">
                        <span>{rupee(amt)}</span>
                        {items.length>1 && <Button size="icon" variant="ghost" onClick={()=>removeRow(i)}><Trash2 size={14} className="text-destructive"/></Button>}
                      </div>
                    </div>
                  );
                })}
                <Button variant="outline" size="sm" onClick={addRow} data-testid="add-row-btn"><Plus size={14} className="mr-2"/>Add row</Button>
              </Card>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Notes</Label>
                  <Textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3} />
                  <div className="mt-3">
                    <Label>Shipping (₹)</Label>
                    <Input type="number" value={shipping} onChange={e=>setShipping(e.target.value)} />
                  </div>
                </div>
                <Card className="p-4 bg-secondary/50 border-border">
                  <div className="flex justify-between text-sm py-1"><span>Subtotal</span><span>{rupee(totals.sub)}</span></div>
                  <div className="flex justify-between text-sm py-1"><span>Discount</span><span>- {rupee(totals.disc)}</span></div>
                  {applyTax ? (
                    isIgst ? (
                      <div className="flex justify-between text-sm py-1"><span>IGST</span><span>{rupee(totals.tax)}</span></div>
                    ) : (
                      <>
                        <div className="flex justify-between text-sm py-1"><span>CGST</span><span>{rupee(totals.tax/2)}</span></div>
                        <div className="flex justify-between text-sm py-1"><span>SGST</span><span>{rupee(totals.tax/2)}</span></div>
                      </>
                    )
                  ) : (
                    <div className="flex justify-between text-sm py-1 text-muted-foreground"><span>Tax</span><span>Not applicable</span></div>
                  )}
                  {Number(shipping)>0 && <div className="flex justify-between text-sm py-1"><span>Shipping</span><span>{rupee(shipping)}</span></div>}
                  <div className="flex justify-between text-lg py-2 mt-2 border-t hairline font-serif-elegant"><span>Total</span><span className="text-primary font-semibold">{rupee(totals.total)}</span></div>
                  {useWallet && Number(walletAmount||0) > 0 && (
                    <>
                      <div className="flex justify-between text-sm py-1 text-primary"><span>Wallet applied</span><span>- {rupee(walletAmount)}</span></div>
                      <div className="flex justify-between text-sm py-1 font-medium"><span>Remaining to collect</span><span>{rupee(remainingAfterWallet)}</span></div>
                    </>
                  )}
                </Card>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                <div>
                  <Label>Payment received now</Label>
                  <Select value={paymentMode} onValueChange={(v)=>{ setPaymentMode(v); if (v!=="none" && !paymentAmount) setPaymentAmount(String(remainingAfterWallet)); }}>
                    <SelectTrigger data-testid="invoice-payment-mode"><SelectValue placeholder="Not paid yet" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not paid yet</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="bank">Bank transfer</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {paymentMode !== "none" && (
                  <div>
                    <Label>Amount received</Label>
                    <Input type="number" value={paymentAmount} onChange={e=>setPaymentAmount(e.target.value)} data-testid="invoice-payment-amount" />
                  </div>
                )}
              </div>

            </div>
            <DialogFooter><Button onClick={create} disabled={submitting} className="bg-primary hover:bg-[#984D38]" data-testid="create-invoice-submit">{submitting ? "Creating…" : "Create Invoice"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 border-border bg-white">
        <div className="flex gap-3 flex-wrap items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search invoice number or customer…" className="pl-9" value={search} onChange={e=>setSearch(e.target.value)} data-testid="invoice-search" />
          </div>
          <Select value={statusF} onValueChange={setStatusF}>
            <SelectTrigger className="w-40" data-testid="status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="unpaid">Unpaid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="returned">Returned</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground ml-auto">{invoices.length} invoices</span>
        </div>
      </Card>

      <Card className="border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground bg-secondary/50">
            <tr>
              <th className="py-3 px-4">Number</th>
              <th className="py-3 px-4">Customer</th>
              <th className="py-3 px-4">Date</th>
              <th className="py-3 px-4">Due</th>
              <th className="py-3 px-4 text-right">Total</th>
              <th className="py-3 px-4 text-right">Paid</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && <tr><td colSpan={8} className="py-12 text-center text-muted-foreground">No invoices yet.</td></tr>}
            {invoices.map(inv => (
              <tr key={inv.id} className="border-t hairline hover:bg-accent/30">
                <td className="py-3 px-4"><Link to={`/invoices/${inv.id}`} className="font-medium hover:text-primary" data-testid={`invoice-link-${inv.id}`}>{inv.number}</Link></td>
                <td className="py-3 px-4">{inv.customer?.name}</td>
                <td className="py-3 px-4 text-muted-foreground">{inv.date}</td>
                <td className="py-3 px-4 text-muted-foreground">{inv.due_date}</td>
                <td className="py-3 px-4 text-right font-medium">{rupee(inv.total)}</td>
                <td className="py-3 px-4 text-right">{rupee(inv.paid_amount)}</td>
                <td className="py-3 px-4"><Badge variant="outline" className={`${statusColor[inv.status]||""} capitalize`}>{inv.status}</Badge></td>
                <td className="py-3 px-4 text-right">
                  <Button size="icon" variant="ghost" onClick={()=>del(inv.id)} data-testid={`delete-invoice-${inv.id}`}><Trash2 size={14} className="text-destructive"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
